import type { BacktestFormState } from "./types.ts";
import type { Locale } from "./homepage-seo.ts";

export const TRADING_METHODS_CATALOG = "/api/v2/trading-methods/catalog";
export const METHOD_UNAVAILABLE = "trading_method_unavailable";
export const METHOD_INVALID = "invalid_trading_method";
export const METHOD_RESULT_MISMATCH = "trading_method_result_mismatch";
export const tradingMethodIDs = ["sweep_reclaim", "double_top_bottom", "ema_crossover", "donchian_breakout", "rsi_reversion", "bollinger_reversion"] as const;
export type TradingMethodID = typeof tradingMethodIDs[number];
export interface TradingMethodSpec {
  schema_version: 1;
  method_id: TradingMethodID;
  method_version: 1;
  parameters: { volume_filter: "none" | "rvol"; rvol_period: number; rvol_min: number } & Record<string, string | number>;
}
export interface TradingMethodCatalogEntry {
  method_id: TradingMethodID; method_version: 1; article_slug: string;
  defaults: Record<string, unknown>; variant: string;
}
export interface TradingMethodCatalog { schema_version: 1; methods: TradingMethodCatalogEntry[] }
export interface MethodAnchor {
  id: string; type: "high" | "low"; index: number; price: number; occurred_at: string; confirmed_at: string;
}
export interface MethodEvent {
  id: string; type: "pivot" | "candidate" | "signal" | "rejection" | "expiry";
  status: "confirmed" | "created" | "cancelled" | "expired" | "rejected";
  direction: "long" | "short" | null; occurred_at: string; confirmed_at: string; signal_at: string | null;
  anchors: MethodAnchor[]; level: number | null; neckline: number | null; stop: number | null;
  atr: number | null; rvol: number | null; reason: string;
}
export interface MethodEvidence {
  schema_version: 1; method_id: TradingMethodID; method_version: 1; config_hash: string;
  parameters: Record<string, unknown>; article_slug: string; variant: string; events: MethodEvent[];
  diagnostics: Record<string, unknown>; research_status: "experimental";
  risk_policy?: string; exit_policy?: string; protective_orders_active?: boolean; execution_policy?: string;
}
const object = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === "object" && !Array.isArray(v);
export const isTradingMethodID = (v: unknown): v is TradingMethodID => typeof v === "string" && (tradingMethodIDs as readonly string[]).includes(v);
const id = isTradingMethodID;
const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const text = (v: unknown, max = 2000): v is string => typeof v === "string" && v.length <= max;
const date = (v: unknown): v is string => text(v, 64) && /T.*(?:Z|[+-]\d\d:\d\d)$/.test(v) && Number.isFinite(Date.parse(v));
const nullableNumber = (v: unknown): v is number | null => v === null || finite(v);
const indicatorDefaults = { atr_period: 14, tick_size: 0, reward_risk: 2 };
const commonDefaults = { pivot_left: 3, pivot_right: 3, atr_period: 14, tick_size: 0, reward_risk: 2 };
export const methodFixedDefaults: Record<TradingMethodID, Record<string, string | number>> = {
  sweep_reclaim: { ...commonDefaults, max_level_age: 100, penetration_atr: .10, reclaim_atr: .15, confirmation: "reclaim_close", confirmation_ttl: 4 },
  double_top_bottom: { ...commonDefaults, min_separation: 7, max_pattern_bars: 250, min_depth_atr: 1.5, tolerance_atr: .15, depth_tolerance: .10, breakout_buffer_atr: .10, min_ttl: 5, prior_trend: "confirmed_pairs" },
  ema_crossover: { ...indicatorDefaults, fast_period: 20, slow_period: 50, stop_atr: 1.5 },
  donchian_breakout: { ...indicatorDefaults, channel_period: 20, breakout_buffer_atr: .1, stop_atr: 2 },
  rsi_reversion: { ...indicatorDefaults, rsi_period: 14, oversold: 30, overbought: 70, stop_atr: 1.5 },
  bollinger_reversion: { ...indicatorDefaults, band_period: 20, band_stddev: 2, stop_atr: 1.5 },
};
export const methodVariants: Record<TradingMethodID, string> = {
  sweep_reclaim: "latest_confirmed_pivot_prior_bar_atr_prior_median_volume_v1",
  double_top_bottom: "alternating_pivots_first_neckline_cross_prior_median_volume_v1",
  ema_crossover: "sma_seeded_ema_close_cross_prior_median_volume_v1",
  donchian_breakout: "prior_channel_close_break_prior_bar_atr_prior_median_volume_v1",
  rsi_reversion: "wilder_rsi_threshold_reentry_prior_median_volume_v1",
  bollinger_reversion: "population_bands_close_reentry_prior_median_volume_v1",
};
export const methodArticleSlugs: Record<TradingMethodID, string> = { sweep_reclaim: "sweep-reclaim", double_top_bottom: "hai-dinh-hai-day", ema_crossover: "", donchian_breakout: "", rsi_reversion: "", bollinger_reversion: "" };

/** Only implemented versions may execute. Invalid saved methods never fall back to a preset. */
export function parseTradingMethodSpec(value: unknown): TradingMethodSpec | null {
  if (!object(value) || value.schema_version !== 1 || !id(value.method_id) || value.method_version !== 1 || !object(value.parameters)) return null;
  const p = value.parameters;
  if (Object.keys(value).some((k) => !["schema_version", "method_id", "method_version", "parameters"].includes(k))
    || Object.keys(p).some((k) => !["volume_filter", "rvol_period", "rvol_min"].includes(k) && (!Object.prototype.hasOwnProperty.call(methodFixedDefaults[value.method_id as TradingMethodID], k) || methodFixedDefaults[value.method_id as TradingMethodID][k] !== p[k]))
    || (p.volume_filter !== "none" && p.volume_filter !== "rvol")
    || !finite(p.rvol_period) || !Number.isInteger(p.rvol_period) || p.rvol_period < 2 || p.rvol_period > 250
    || !finite(p.rvol_min) || p.rvol_min < .1 || p.rvol_min > 20) return null;
  return { schema_version: 1, method_id: value.method_id, method_version: 1,
    parameters: { ...p, volume_filter: p.volume_filter, rvol_period: p.rvol_period, rvol_min: p.rvol_min } as TradingMethodSpec["parameters"] };
}
export function defaultTradingMethod(method_id: TradingMethodID): TradingMethodSpec {
  return { schema_version: 1, method_id, method_version: 1, parameters: { volume_filter: "none", rvol_period: 20, rvol_min: 1.5 } };
}
export function selectTradingMethod(current: BacktestFormState, spec: TradingMethodSpec): BacktestFormState {
  const parsed = parseTradingMethodSpec(spec);
  if (!parsed) throw new Error(METHOD_INVALID);
  return { ...current, strategyMode: "method", strategyMethod: parsed, unsupportedMethodExecution: undefined, strategyDSL: undefined,
    strategyName: parsed.method_id, prompt: `Research method: ${parsed.method_id} v${parsed.method_version}`, useAI: false, exitMode: "tp_sl_only" };
}
export function supportsTradingMethods(value: unknown, method?: { method_id: TradingMethodID; method_version: number }): boolean {
  if (!object(value) || !object(value.capabilities) || !object(value.capabilities.trading_methods)) return false;
  const c = value.capabilities.trading_methods;
  return c.enabled === true && Array.isArray(c.schema_versions) && c.schema_versions.includes(1)
    && (c.catalog_url === undefined || c.catalog_url === TRADING_METHODS_CATALOG)
    && object(c.method_versions)
    && (method ? Array.isArray(c.method_versions[method.method_id]) && (c.method_versions[method.method_id] as unknown[]).includes(method.method_version)
      : tradingMethodIDs.some((m) => Array.isArray((c.method_versions as Record<string, unknown>)[m]) && ((c.method_versions as Record<string, unknown>)[m] as unknown[]).includes(1)));
}
export function parseTradingMethodCatalog(value: unknown): TradingMethodCatalog | null {
  if (!object(value) || value.schema_version !== 1 || !Array.isArray(value.methods) || value.methods.length > 100) return null;
  const methods: TradingMethodCatalogEntry[] = [];
  for (const item of value.methods) {
    if (!object(item)) return null;
    // Future methods can coexist; never expose them before this client supports them.
    if (!id(item.method_id) || item.method_version !== 1) continue;
    if (methods.some((m) => m.method_id === item.method_id) || item.article_slug !== methodArticleSlugs[item.method_id]
      || item.research_status !== "experimental" || !object(item.defaults) || item.variant !== methodVariants[item.method_id]
      || !Object.entries(methodFixedDefaults[item.method_id]).every(([k, v]) => (item.defaults as Record<string, unknown>)[k] === v)) return null;
    methods.push({ method_id: item.method_id, method_version: 1, article_slug: item.article_slug, defaults: { ...item.defaults }, variant: item.variant });
  }
  return methods.length ? { schema_version: 1, methods } : null;
}
export function methodArticleURL(method: TradingMethodID, locale: Locale): string | undefined {
  if (!methodArticleSlugs[method]) return undefined;
  return `${locale === "en" ? "" : `/${locale}`}/learn/method-${methodArticleSlugs[method]}/`;
}
export function parseMethodEvidence(value: unknown): MethodEvidence | null {
  if (!object(value) || value.schema_version !== 1 || !id(value.method_id) || value.method_version !== 1
    || value.article_slug !== methodArticleSlugs[value.method_id] || value.research_status !== "experimental"
    || !text(value.config_hash, 128) || !/^[a-f0-9]{64}$/.test(value.config_hash)
    || !object(value.parameters) || !object(value.diagnostics) || value.variant !== methodVariants[value.method_id]
    || !Array.isArray(value.events) || value.events.length > 100_000) return null;
  if ([value.risk_policy, value.exit_policy, value.execution_policy].some((p) => p !== undefined && !text(p, 200))
    || value.protective_orders_active !== undefined && typeof value.protective_orders_active !== "boolean") return null;
  const seen = new Set<string>();
  for (const e of value.events) {
    if (!object(e) || !text(e.id, 200) || seen.has(e.id) || !["pivot", "candidate", "signal", "rejection", "expiry"].includes(String(e.type))
      || !["confirmed", "created", "cancelled", "expired", "rejected"].includes(String(e.status))
      || !["long", "short", null].includes(e.direction as string | null)
      || !date(e.occurred_at) || !date(e.confirmed_at) || Date.parse(e.confirmed_at) < Date.parse(e.occurred_at)
      || !(e.signal_at === null || date(e.signal_at) && Date.parse(e.signal_at) >= Date.parse(e.confirmed_at))
      || !text(e.reason) || ![e.level, e.neckline, e.stop, e.atr, e.rvol].every(nullableNumber)
      || !Array.isArray(e.anchors) || e.anchors.length > 20) return null;
    if (e.type === "signal" && (e.signal_at === null || e.direction === null)) return null;
    for (const a of e.anchors) {
      if (!object(a) || !text(a.id, 200) || (a.type !== "high" && a.type !== "low") || !finite(a.index) || !Number.isInteger(a.index) || a.index < 0
        || !finite(a.price) || !date(a.occurred_at) || !date(a.confirmed_at)
        || Date.parse(a.confirmed_at) < Date.parse(a.occurred_at) || Date.parse(a.confirmed_at) > Date.parse(e.confirmed_at)) return null;
    }
    seen.add(e.id);
  }
  // Network objects are isolated from subsequently edited form state.
  return JSON.parse(JSON.stringify(value)) as MethodEvidence;
}
/** Evidence can contain supported server parameters beyond this editor's fixed profiles. */
export function methodEvidenceUsesFixedRules(evidence: Pick<MethodEvidence, "method_id" | "parameters">): boolean {
  return Object.entries(methodFixedDefaults[evidence.method_id]).every(([key, value]) => evidence.parameters[key] === value);
}
export function matchesMethodResult(spec: TradingMethodSpec, evidence: MethodEvidence | null): boolean {
  return Boolean(evidence && evidence.method_id === spec.method_id && evidence.method_version === spec.method_version
    && Object.entries({ ...methodFixedDefaults[spec.method_id], ...spec.parameters }).every(([key, value]) => evidence.parameters[key] === value));
}
/** Mark availability, never the earlier geometric pivot timestamp. */
export function methodSignalMarkers(value: unknown, candleTimes: readonly string[]): Array<{ time: number; direction: "long" | "short" }> {
  const evidence = parseMethodEvidence(value);
  if (!evidence) return [];
  const candles = new Set(candleTimes.map((t) => Math.floor(Date.parse(t) / 1000)));
  return evidence.events.filter((e) => e.type === "signal" && e.status === "confirmed" && e.direction && e.signal_at)
    .map((e) => ({ time: Math.floor(Date.parse(e.signal_at!) / 1000), direction: e.direction as "long" | "short" }))
    .filter((e) => candles.has(e.time)).sort((a, b) => a.time - b.time);
}
